import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, X, Save, Trash2, FolderPlus, Pencil } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { v: "0", label: "Montag" },
  { v: "1", label: "Dienstag" },
  { v: "2", label: "Mittwoch" },
  { v: "3", label: "Donnerstag" },
  { v: "4", label: "Freitag" },
  { v: "5", label: "Samstag" },
  { v: "6", label: "Sonntag" },
  { v: "holiday", label: "Feiertag" },
];

const emptyBlock = () => ({ start_time: "08:00", end_time: "16:00", breaks: [] });

// Normalize a stored day (legacy single start/end + breaks, or new blocks[]) into { active, blocks }.
const normalizeDay = (raw) => {
  const d = raw || {};
  const mkBreaks = (arr) => (arr || []).map((x) => ({ start: x.start || "", end: x.end || "" }));
  let blocks;
  if (Array.isArray(d.blocks) && d.blocks.length) {
    blocks = d.blocks.map((b) => ({
      start_time: b.start_time || "08:00",
      end_time: b.end_time || "16:00",
      breaks: mkBreaks(b.breaks),
    }));
  } else {
    blocks = [{
      start_time: d.start_time || "08:00",
      end_time: d.end_time || "16:00",
      breaks: mkBreaks(d.breaks),
    }];
  }
  return { active: !!d.active, blocks };
};

export default function Schedule() {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [sched, setSched] = useState({});

  // dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState("");

  const cat = useMemo(() => cats.find((c) => c.id === catId), [cats, catId]);

  const loadCats = async (preferId) => {
    try {
      const { data } = await api.get("/categories");
      setCats(data);
      const next = preferId && data.find((c) => c.id === preferId) ? preferId
        : (data.find((c) => c.id === catId) ? catId : (data[0]?.id || ""));
      setCatId(next);
    } catch (e) { if (e?.response?.status !== 401) toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadCats(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!cat) return;
    const raw = cat.schedules || {};
    const norm = {};
    DAYS.forEach(({ v }) => { norm[v] = normalizeDay(raw[v]); });
    setSched(norm);
  }, [cat]);

  const getDay = (v) => sched[v] || normalizeDay(null);
  const getDayFrom = (s, v) => s[v] || normalizeDay(null);

  const setActive = (v, val) =>
    setSched((s) => ({ ...s, [v]: { ...getDayFrom(s, v), active: val } }));

  // ---- Work block helpers ----
  const addBlock = (v) =>
    setSched((s) => { const d = getDayFrom(s, v); return { ...s, [v]: { ...d, blocks: [...d.blocks, emptyBlock()] } }; });
  const rmBlock = (v, bi) =>
    setSched((s) => { const d = getDayFrom(s, v); return { ...s, [v]: { ...d, blocks: d.blocks.length <= 1 ? d.blocks : d.blocks.filter((_, i) => i !== bi) } }; });
  const updBlockField = (v, bi, k, val) =>
    setSched((s) => {
      const d = getDayFrom(s, v);
      const blocks = d.blocks.map((b, i) => (i === bi ? { ...b, [k]: val } : b));
      return { ...s, [v]: { ...d, blocks } };
    });

  // ---- Break helpers (within a block) ----
  const addBreak = (v, bi) =>
    setSched((s) => {
      const d = getDayFrom(s, v);
      const blocks = d.blocks.map((b, i) => (i === bi ? { ...b, breaks: [...(b.breaks || []), { start: "12:00", end: "12:30" }] } : b));
      return { ...s, [v]: { ...d, blocks } };
    });
  const rmBreak = (v, bi, i) =>
    setSched((s) => {
      const d = getDayFrom(s, v);
      const blocks = d.blocks.map((b, idx) => (idx === bi ? { ...b, breaks: (b.breaks || []).filter((_, j) => j !== i) } : b));
      return { ...s, [v]: { ...d, blocks } };
    });
  const updBreak = (v, bi, i, k, val) =>
    setSched((s) => {
      const d = getDayFrom(s, v);
      const blocks = d.blocks.map((b, idx) => {
        if (idx !== bi) return b;
        const breaks = [...(b.breaks || [])];
        breaks[i] = { ...breaks[i], [k]: val };
        return { ...b, breaks };
      });
      return { ...s, [v]: { ...d, blocks } };
    });

  const saveDay = async (v) => {
    if (!catId) return;
    const d = getDay(v);
    const blocks = d.blocks.map((b) => ({
      start_time: b.start_time,
      end_time: b.end_time,
      breaks: (b.breaks || []).filter((x) => x.start && x.end),
    }));
    const first = blocks[0] || emptyBlock();
    const payload = {
      active: d.active,
      start_time: first.start_time,   // mirror first block for backward compatibility
      end_time: first.end_time,
      breaks: first.breaks,
      blocks,
    };
    try {
      await api.patch(`/categories/${catId}/schedule/${v}`, payload);
      toast.success("Gespeichert");
      setCats((arr) => arr.map((c) => c.id === catId ? { ...c, schedules: { ...c.schedules, [v]: { ...payload } } } : c));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createCategory = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Bitte Namen eingeben"); return; }
    try {
      const { data } = await api.post("/categories", { name });
      toast.success("Kategorie angelegt");
      setNewOpen(false); setNewName("");
      await loadCats(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const renameCategory = async () => {
    const name = renameVal.trim();
    if (!name) { toast.error("Bitte Namen eingeben"); return; }
    try {
      await api.patch(`/categories/${catId}`, { name });
      toast.success("Umbenannt");
      setRenameOpen(false);
      await loadCats(catId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteCategory = async () => {
    if (!cat) return;
    if (!window.confirm(`Kategorie „${cat.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/categories/${catId}`);
      toast.success("Kategorie gelöscht");
      await loadCats();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Arbeitszeitmodell</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Legen Sie pro Zeitkonto-Kategorie ein Arbeitszeitmodell mit Arbeits- und Pausenzeiten je Wochentag (und für Feiertage) fest.
            Das Modell dient ausschließlich als Vorlage/Sollvorgabe – Arbeitszeiten werden nicht automatisch erzeugt, sondern manuell erfasst oder über die Start/Stopp-Uhr der Fahrer.
          </p>
        </div>
        <Button onClick={() => { setNewName(""); setNewOpen(true); }} data-testid="category-create-button">
          <FolderPlus className="h-4 w-4 mr-1" /> Neue Kategorie
        </Button>
      </div>

      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">Zeitkonto-Kategorie:</span>
        <Select value={catId} onValueChange={setCatId}>
          <SelectTrigger className="w-72" data-testid="category-select"><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
          <SelectContent>
            {cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.id}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {cat && (
          <>
            <Button variant="outline" size="sm" onClick={() => { setRenameVal(cat.name); setRenameOpen(true); }} data-testid="category-rename-button">
              <Pencil className="h-4 w-4 mr-1" /> Umbenennen
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 border-red-300" onClick={deleteCategory} data-testid="category-delete-button">
              <Trash2 className="h-4 w-4 mr-1" /> Löschen
            </Button>
          </>
        )}
      </Card>

      {!cat ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Keine Kategorie ausgewählt. Legen Sie eine neue Kategorie an.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DAYS.map(({ v, label }) => {
            const s = getDay(v);
            const isHoliday = v === "holiday";
            return (
              <Card key={v} className={`p-4 space-y-3 ${isHoliday ? "border-amber-300 dark:border-amber-700" : ""}`} data-testid={`schedule-card-${v}`}>
                <div className="flex items-center justify-between">
                  <h2 className={`text-base font-semibold heading ${isHoliday ? "text-amber-700 dark:text-amber-300" : ""}`}>{label}</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{s.active ? "Arbeitstag" : "Frei"}</span>
                    <Switch checked={!!s.active} onCheckedChange={(val) => setActive(v, val)} data-testid={`schedule-active-${v}`} />
                  </div>
                </div>

                {/* Work blocks */}
                <div className="space-y-3">
                  {s.blocks.map((blk, bi) => (
                    <div key={bi} className="rounded-md border border-border bg-muted/30 p-3 space-y-2" data-testid={`schedule-block-${v}-${bi}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {s.blocks.length > 1 ? `Zeitblock ${bi + 1}` : "Arbeitszeit"}
                        </span>
                        {s.blocks.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => rmBlock(v, bi)} aria-label="Zeitblock entfernen" data-testid={`schedule-block-remove-${v}-${bi}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Start</Label>
                          <Input type="time" value={blk.start_time || ""} onChange={(e) => updBlockField(v, bi, "start_time", e.target.value)} data-testid={`schedule-start-${v}-${bi}`} />
                        </div>
                        <div>
                          <Label className="text-xs">Ende</Label>
                          <Input type="time" value={blk.end_time || ""} onChange={(e) => updBlockField(v, bi, "end_time", e.target.value)} data-testid={`schedule-end-${v}-${bi}`} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs">Pausen</Label>
                          <Button type="button" variant="ghost" size="sm" onClick={() => addBreak(v, bi)} data-testid={`schedule-break-add-${v}-${bi}`}>
                            <Plus className="h-3 w-3 mr-1" /> hinzufügen
                          </Button>
                        </div>
                        {(blk.breaks || []).length === 0 && <div className="text-xs text-muted-foreground italic">Keine Pausen.</div>}
                        <div className="space-y-2">
                          {(blk.breaks || []).map((b, i) => (
                            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-center" data-testid={`schedule-break-${v}-${bi}-${i}`}>
                              <Input type="time" value={b.start || ""} onChange={(e) => updBreak(v, bi, i, "start", e.target.value)} />
                              <Input type="time" value={b.end || ""} onChange={(e) => updBreak(v, bi, i, "end", e.target.value)} />
                              <Button type="button" size="icon" variant="ghost" className="text-red-600" onClick={() => rmBreak(v, bi, i)} aria-label="Pause entfernen">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => addBlock(v)} data-testid={`schedule-block-add-${v}`}>
                  <Plus className="h-4 w-4 mr-1" /> Weiterer Zeitblock
                </Button>

                <Button onClick={() => saveDay(v)} className="w-full" data-testid={`schedule-save-${v}`}>
                  <Save className="h-4 w-4 mr-1" /> Speichern
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {/* New category dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neue Zeitkonto-Kategorie</DialogTitle>
            <DialogDescription>Die Kategorie startet mit Standard-Zeiten, die Sie anschließend anpassen können.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Name</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z. B. Teilzeit, Vollzeit, Nachtschicht" data-testid="category-name-input" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Abbrechen</Button>
            <Button onClick={createCategory} data-testid="category-name-save">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Kategorie umbenennen</DialogTitle></DialogHeader>
          <div>
            <Label>Name</Label>
            <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid="category-rename-input" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Abbrechen</Button>
            <Button onClick={renameCategory} data-testid="category-rename-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
